import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import {SchemaUtils} from "../../util/SchemaUtils";
import {FormControl} from '@mui/material';
import Date from "./Date";
import {DateUtils} from "../../util/DateUtils";
import {Moment} from "moment";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { ValidationUtil } from '../../util/ValidationUtil';
import { useMemo, useCallback } from 'react';

interface DateControlProps {
    data: any;
    label: any;
    description?: any;
    handleChange(path: string, value: any): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const DateControl = ({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: DateControlProps) => {

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() => {
        const rawValue = ControlValueFactory.getControlValue(Component.Date, data, uischema.scope, jsonForms, enabled && visible);
        return DateUtils.getMomentValue(rawValue);
    }, [Component.Date, data, uischema.scope, jsonForms, enabled, visible]);

    // Clear error if value is empty (but only if it's not a required field error)
    const sanitizedError = useMemo(() => {
        // Check if the raw data is empty vs. invalid
        const rawValue = ControlValueFactory.getControlValue(Component.Date, data, uischema.scope, jsonForms, enabled && visible);
        const isFieldEmpty = ValidationUtil.isEmptyValue(rawValue);

        if (isFieldEmpty && errors) {
            // Only clear error if it's not a required field error
            const isRequiredError = typeof errors === 'string' &&
                (errors.toLowerCase().includes('required'));
            return isRequiredError ? errors : "";
        }
        return errors;
    }, [errors, data, uischema.scope, jsonForms, enabled, visible]);

    // Memoize event handlers
    const handleFocus = useCallback((ev: React.FocusEvent<HTMLInputElement>) => {
        if(ev.target.value === ''){
            handleChange(path, '');
        }
    }, [handleChange, path]);

    const handleKeyUp = useCallback((ev: React.KeyboardEvent<HTMLInputElement>) => {
        // Optional: add keyup logic if needed
    }, []);

    const handleDateChange = useCallback((moment: Moment) => {
        const value = moment?.format('YYYY-MM-DD') ?? "";
        handleChange(path, value);
    }, [handleChange, path]);

    // Memoize the Date component props
    const dateProps: any = useMemo(() => ({
        variant: 'outlined' as const,
        error: sanitizedError,
        value: value,
        required: required,
        label: uischema.label,
        description: schema.description,
        onChange: handleDateChange,
        onFocus: handleFocus,
        onKeyUp: handleKeyUp,
        format: DateUtils.getLongDateFormat(),
        enabled: enabled,
        visible: visible,
        appValidated: appValidated
    }), [
        sanitizedError,
        value,
        required,
        uischema.label,
        schema.description,
        handleDateChange,
        handleFocus,
        handleKeyUp,
        enabled,
        visible,
        appValidated
    ]);

    return (
        <FormControl className="form-control" fullWidth={true}>
            <Date {...dateProps} />
        </FormControl>
    );
};

// Export with memoization
export default React.memo(withJsonFormsControlProps(DateControl));
